import {
ArrowLeft,
BadgeCheck,
BarChart3,
Beef,
Camera,
Check,
ChevronRight,
Clock3,
Droplets,
Edit3,
FileText,
Flame,
Leaf,
Loader2,
Mic,
MoreHorizontal,
Plus,
Scale,
Sparkles,
Square,
Trash2,
Trophy,
UtensilsCrossed,
Wheat,
X
} from 'lucide-react';
import React,{ useCallback,useEffect,useRef,useState } from 'react';
import {
FrequentMealItem,
MiniAppActivity,
useApi
} from '../../services/api';
import { ACTIVITY_INTENSITY_LABELS,ACTIVITY_TYPE_META,AuthorizedMealImage,MAX_MEAL_PORTION_PERCENT,MEAL_ACTION_LONG_PRESS_DELAY_MS,MEAL_PORTION_STEP,MIN_MEAL_PORTION_PERCENT,MealVisualPlaceholder,TOUCH_HOLD_PROTECTION_STYLE,V2Meal,clampMealConsumedPercent,formatDurationMinutes,formatIngredientMacroLine,formatLongDate,formatShortTime,getMealConsumedPercent,getMealIngredients,getMealInsightSummary,getMealPhotoUrl,getMealPortionPreview,getMealTypeLabel,getMealVisualKind,getMicronutrientRows,getSelectablePillClass,mealTypeDefinitions,roundNumber,useAuthorizedMealImage,useEdgeSwipeBack,useHorizontalSwipeGesture,useMiniAppAuth,useMiniAppBackNavigation } from '../shared';

export function MealDetailScreen({
  meal,
  onBack,
  onDelete,
  onUpdateWeight,
  isDeleting,
  isUpdatingWeight,
  themeMode,
}: {
  meal: V2Meal;
  onBack: () => void;
  onDelete: (meal: V2Meal) => void;
  onUpdateWeight: (meal: V2Meal, target: { kind: 'meal' } | { kind: 'ingredient'; index: number }, weightGrams: number) => Promise<void>;
  isDeleting: boolean;
  isUpdatingWeight: boolean;
  themeMode: 'light' | 'dark';
}) {
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [weightEditor, setWeightEditor] = useState<{ target: { kind: 'meal' } | { kind: 'ingredient'; index: number }; title: string; currentWeight: number; calories: number } | null>(null);
  const [weightDraft, setWeightDraft] = useState('');
  const { requestBack } = useMiniAppBackNavigation(`meal:${meal.id}`, onBack);
  const swipeBackHandlers = useEdgeSwipeBack(true, requestBack);
  const ingredients = getMealIngredients(meal);
  const mealPhotoUrl = getMealPhotoUrl(meal);
  const mealPhoto = useAuthorizedMealImage(mealPhotoUrl);
  const mealVisualKind = getMealVisualKind(meal);
  const insights = getMealInsightSummary(meal);
  const consumedPercent = getMealConsumedPercent(meal);
  const hasPortionAdjustment = consumedPercent < 100;
  const totalWeight = ingredients.reduce((sum, item) => sum + Number(item.weight || 0), 0) || roundNumber(meal.weight);
  const subtitle = ingredients.length > 0
    ? `${ingredients[0].name}${ingredients.length > 1 ? `, еще ${ingredients.length - 1}` : ''}${totalWeight > 0 ? ` • ${totalWeight} г` : ''}`
    : `${getMealTypeLabel(meal.category)}${totalWeight > 0 ? ` • ${totalWeight} г` : ''}`;
  const fiberValue = meal.fiber == null
    ? roundNumber(ingredients.reduce((sum, item) => sum + Number(item.fiber || 0), 0))
    : roundNumber(meal.fiber);

  const insightCards = [
    {
      title: `Оценка ${insights.overallScore}/10`,
      description: insights.qualitySummary,
      icon: <Trophy className="h-6 w-6 text-[#7f54ff]" />,
      shell: 'bg-[#f0ebff]',
    },
    {
      title: 'Когда лучше',
      description: insights.timingComment,
      icon: <Clock3 className="h-6 w-6 text-[#4f88ff]" />,
      shell: 'bg-[#edf4ff]',
    },
    {
      title: 'Гликемический индекс',
      description: insights.glycemicSummary,
      icon: <BarChart3 className="h-6 w-6 text-[#5b8cff]" />,
      shell: 'bg-[#edf3ff]',
    },
    {
      title: 'Польза',
      description: insights.benefitSummary,
      icon: <BadgeCheck className="h-6 w-6 text-[#5ac26a]" />,
      shell: 'bg-[#edf8ef]',
    },
  ];

  const microRows = getMicronutrientRows(meal.micronutrients);
  const nutritionCards = [
    { label: 'Белки', value: roundNumber(meal.protein), unit: 'грамм', color: '#7f54ff', icon: <Beef className="h-4 w-4 text-[#7f54ff]" /> },
    { label: 'Жиры', value: roundNumber(meal.fats), unit: 'грамм', color: '#e2b927', icon: <Droplets className="h-4 w-4 text-[#d2ab1a]" /> },
    { label: 'Углеводы', value: roundNumber(meal.carbs), unit: 'грамм', color: '#8d6bff', icon: <Wheat className="h-4 w-4 text-[#8d6bff]" /> },
    { label: 'Клетчатка', value: fiberValue, unit: 'грамм', color: '#61c774', icon: <Leaf className="h-4 w-4 text-[#61c774]" /> },
  ];

  return (
    <div {...swipeBackHandlers} className="noma-v2-screen noma-v2-meal-detail pb-10 pt-4">
      <header className="flex items-center px-4 pt-2">
        <button type="button" onClick={requestBack} className="noma-v2-circle-button">
          <ArrowLeft className="h-5 w-5" />
        </button>
      </header>

      <section className="px-4">
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={() => {
              if (mealPhoto.resolvedSrc) setIsImageOpen(true);
            }}
            disabled={!mealPhoto.hasSource}
            className="noma-v2-hero-photo flex h-[132px] w-[132px] items-center justify-center overflow-hidden rounded-full"
          >
            <div className="h-full w-full overflow-hidden rounded-full">
              <AuthorizedMealImage
                src={mealPhotoUrl}
                alt={meal.name}
                className="h-full w-full object-cover"
                loadingFallback={<Loader2 className="h-7 w-7 animate-spin text-[#7f54ff]" />}
                fallback={<MealVisualPlaceholder kind={mealVisualKind} size="hero" />}
              />
            </div>
          </button>
        </div>

        <div className="mt-6 text-center">
          <h1 className="text-[34px] font-black tracking-[-0.06em] text-[#111113]">{meal.name}</h1>
          <div className="mt-2 text-[14px] font-bold uppercase tracking-[0.06em] text-[#8a8a96]">
            {subtitle}
          </div>
          {totalWeight > 0 ? (
            <button type="button" onClick={() => { setWeightEditor({ target: { kind: 'meal' }, title: meal.name, currentWeight: totalWeight, calories: Number(meal.calories || 0) }); setWeightDraft(String(roundNumber(totalWeight))); }} className="noma-v2-meal-weight-button mt-3 inline-flex items-center gap-2 rounded-full bg-[#f1edff] px-4 py-2 text-[13px] font-extrabold text-[#7048e8]">
              <Scale className="h-4 w-4" /> Изменить вес блюда
            </button>
          ) : null}
          {hasPortionAdjustment ? (
            <div className="mt-3 inline-flex items-center rounded-full bg-[rgba(127,84,255,0.1)] px-3 py-1 text-[12px] font-extrabold uppercase tracking-[0.08em] text-[#7f54ff]">
              Съедено {consumedPercent}%
            </div>
          ) : null}
        </div>

        <article className="noma-v2-card mt-6 p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-[16px] bg-[#efe7ff]">
                <Flame className="h-5 w-5 text-[#7f54ff]" />
              </div>
              <div>
                <div className="text-[16px] font-extrabold tracking-[-0.04em] text-[#111113]">Калории</div>
                <div className="text-[13px] font-semibold leading-5 text-[#868692]">Энергетическая ценность</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[22px] font-black tracking-[-0.05em] text-[#111113]">{roundNumber(meal.calories)}</div>
              <div className="text-[12px] font-bold uppercase tracking-[0.14em] text-[#9a9aa6]">ккал</div>
            </div>
          </div>
        </article>

        <div className="mt-4 grid grid-cols-2 gap-3">
          {nutritionCards.map((card) => (
            <article key={card.label} className="noma-v2-card p-4">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f4f4f8]">
                  {card.icon}
                </div>
                <div className="text-[15px] font-extrabold tracking-[-0.03em] text-[#111113]">{card.label}</div>
              </div>
              <div className="mt-5 flex items-end justify-between gap-3">
                <div className="text-[24px] font-black tracking-[-0.05em] text-[#111113]">{card.value}</div>
                <div className="text-[12px] font-bold text-[#a1a1ab]">{card.unit}</div>
              </div>
              <div className="mt-4 h-1.5 rounded-full bg-[#f1f1f5]">
                <div
                  className="h-1.5 rounded-full"
                  style={{ width: `${Math.max(18, Math.min(100, card.value * 2))}%`, background: card.color }}
                />
              </div>
            </article>
          ))}
        </div>

        {microRows.length > 0 ? (
          <article className="mt-3 noma-v2-card p-4">
            <div className="text-[12px] font-black uppercase tracking-[0.1em] text-[#9a9aa6]">Микроэлементы</div>
            <div className="mt-2.5 grid grid-cols-3 gap-2">
              {microRows.map((row) => (
                <div key={row.key} className="flex items-center gap-1.5 rounded-[12px] bg-[#f7f7fb] px-2 py-1.5">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full" style={{ background: `${row.color}22` }}>
                    {row.icon}
                  </span>
                  <div className="min-w-0 leading-tight">
                    <div className="truncate text-[12px] font-extrabold text-[#111113]">
                      {row.value}<span className="text-[9px] font-bold text-[#a1a1ab]"> {row.unit}</span>
                    </div>
                    <div className="truncate text-[9px] font-semibold text-[#9a9aa6]">{row.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </article>
        ) : null}

        <section className="mt-6">
          <div className="space-y-4">
            {insightCards.map((card) => (
              <article key={card.title} className="noma-v2-card p-6">
                <div className="flex items-start gap-4">
                  <div className={`noma-v2-meal-insight-icon flex h-14 w-14 shrink-0 items-center justify-center rounded-full ${card.shell}`}>
                    {card.icon}
                  </div>
                  <div>
                    <h2 className="text-[22px] font-black tracking-[-0.05em] text-[#111113]">{card.title}</h2>
                    <p className="mt-2 text-[15px] font-semibold leading-8 text-[#a0a0aa]">
                      {card.description}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-8">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-[34px] font-black tracking-[-0.06em] text-[#111113]">Состав</h2>
          </div>

          <article className="noma-v2-card overflow-hidden p-2">
            {ingredients.map((ingredient, index) => (
              <button type="button" key={ingredient.id} onClick={() => { const currentWeight = Number(ingredient.weight || 0); if (currentWeight > 0) { setWeightEditor({ target: { kind: 'ingredient', index }, title: ingredient.name, currentWeight, calories: Number(ingredient.calories || 0) }); setWeightDraft(String(roundNumber(currentWeight))); } }} className={`${index > 0 ? 'border-t border-[rgba(17,17,19,0.06)]' : ''} flex w-full items-center justify-between gap-4 px-3 py-4 text-left disabled:opacity-60`} disabled={!ingredient.weight || isUpdatingWeight}>
                <div className="flex min-w-0 items-center gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[18px] bg-[#f4f4f8] text-[20px] font-black tracking-[-0.04em] text-[#9f9faa]">
                    {ingredient.name.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-[19px] font-black tracking-[-0.05em] text-[#111113]">{ingredient.name}</div>
                    {formatIngredientMacroLine(ingredient) ? (
                      <div className="mt-1 text-[12px] font-medium tracking-[-0.02em] text-[#8f909b]">
                        {formatIngredientMacroLine(ingredient)}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1 text-[18px] font-black tracking-[-0.04em] text-[#9999a4]">
                  {ingredient.weight ? `${ingredient.weight} г` : '—'} {ingredient.weight ? <ChevronRight className="h-4 w-4" /> : null}
                </div>
              </button>
            ))}
          </article>
        </section>

        <section className="mt-8">
          <button
            type="button"
            onClick={() => void onDelete(meal)}
            disabled={isDeleting}
            className={`flex min-h-[56px] w-full items-center justify-center gap-2 rounded-[20px] border px-5 text-[15px] font-extrabold tracking-[-0.03em] disabled:opacity-60 ${
              themeMode === 'dark'
                ? 'border-[rgba(227,84,84,0.22)] bg-[rgba(217,75,75,0.12)] text-[#ff8f8f] shadow-[0_14px_28px_rgba(0,0,0,0.18)]'
                : 'border-[rgba(227,84,84,0.18)] bg-[rgba(255,255,255,0.86)] text-[#d44949] shadow-[0_14px_28px_rgba(33,26,59,0.05)]'
            }`}
          >
            {isDeleting ? <Loader2 className="h-4.5 w-4.5 animate-spin" /> : <Trash2 className="h-4.5 w-4.5" />}
            <span>Удалить прием</span>
          </button>
        </section>
      </section>

      {isImageOpen && mealPhoto.resolvedSrc ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(17,17,19,0.96)] px-4 py-8">
          <button
            type="button"
            onClick={() => setIsImageOpen(false)}
            className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/12 text-white"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="w-full max-w-[560px]">
            <img src={mealPhoto.resolvedSrc} alt={meal.name} className="max-h-[82vh] w-full rounded-[28px] object-contain" />
          </div>
        </div>
      ) : null}

      {weightEditor ? (
        <div className="noma-v2-modal-backdrop fixed inset-0 z-[76] flex items-end justify-center bg-[rgba(17,17,19,0.34)] px-4 pb-4 pt-10">
          <button type="button" aria-label="Закрыть" className="absolute inset-0" onClick={() => { if (!isUpdatingWeight) setWeightEditor(null); }} />
          <div className={`noma-v2-sheet-enter noma-v2-sheet-enter--bottom relative w-full max-w-[360px] rounded-[30px] p-5 ${themeMode === 'dark' ? 'bg-[#181a21] text-white' : 'bg-white text-[#111113]'}`}>
            <div className="mx-auto h-1.5 w-12 rounded-full bg-current opacity-10" />
            <h2 className="mt-5 text-[25px] font-black tracking-[-0.05em]">{weightEditor.target.kind === 'meal' ? 'Вес блюда' : 'Вес ингредиента'}</h2>
            <p className="mt-1 truncate text-[14px] font-semibold opacity-55">{weightEditor.title}</p>
            <div className={`mt-5 flex items-center rounded-[22px] px-4 ${themeMode === 'dark' ? 'bg-[#232630]' : 'bg-[#f5f4f8]'}`}>
              <input autoFocus type="number" inputMode="decimal" min="1" max="5000" value={weightDraft} onChange={(event) => setWeightDraft(event.target.value)} className="min-w-0 flex-1 bg-transparent py-5 text-center text-[38px] font-black outline-none" />
              <span className="text-[17px] font-extrabold opacity-45">г</span>
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2">
              {[-10, -5, 5, 10].map((delta) => <button key={delta} type="button" disabled={isUpdatingWeight} onClick={() => setWeightDraft(String(Math.max(1, Math.min(5000, Math.round(Number(weightDraft || weightEditor.currentWeight) + delta)))))} className={`min-h-[42px] rounded-[14px] text-[13px] font-black ${themeMode === 'dark' ? 'bg-[#232630]' : 'bg-[#f5f4f8]'}`}>{delta > 0 ? '+' : ''}{delta} г</button>)}
            </div>
            {Number(weightDraft) > 0 ? <div className="mt-4 text-center text-[13px] font-bold opacity-55">Примерно {roundNumber(weightEditor.calories * Number(weightDraft) / weightEditor.currentWeight)} ккал после пересчёта</div> : null}
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button type="button" disabled={isUpdatingWeight} onClick={() => setWeightEditor(null)} className={`min-h-[52px] rounded-[18px] font-extrabold ${themeMode === 'dark' ? 'bg-[#232630]' : 'bg-[#f3f3f7]'}`}>Отмена</button>
              <button type="button" disabled={isUpdatingWeight || !Number.isFinite(Number(weightDraft)) || Number(weightDraft) < 1 || Number(weightDraft) > 5000} onClick={async () => { await onUpdateWeight(meal, weightEditor.target, Math.round(Number(weightDraft))); setWeightEditor(null); }} className="noma-v2-primary-button flex min-h-[52px] items-center justify-center gap-2 rounded-[18px] font-extrabold disabled:opacity-60">{isUpdatingWeight ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Сохранить</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function SwipeableMealRow({
  meal,
  onOpen,
  onLongPress,
  onDelete,
  isDeleting,
  themeMode,
}: {
  meal: V2Meal;
  onOpen: (meal: V2Meal) => void;
  onLongPress: (meal: V2Meal) => void;
  onDelete: (meal: V2Meal) => void;
  isDeleting: boolean;
  themeMode: 'light' | 'dark';
}) {
  const [dragX, setDragX] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isPressing, setIsPressing] = useState(false);
  const dragXRef = useRef(0);
  const startDragXRef = useRef(0);
  const movedRef = useRef(false);
  const longPressTimerRef = useRef<number | null>(null);
  const suppressClickRef = useRef(false);
  const maxReveal = 94;
  const consumedPercent = getMealConsumedPercent(meal);
  const hasPortionAdjustment = consumedPercent < 100;

  const commitPosition = useCallback((nextOpen: boolean) => {
    setIsOpen(nextOpen);
    const nextX = nextOpen ? -maxReveal : 0;
    dragXRef.current = nextX;
    setDragX(nextX);
  }, []);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current != null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleTouchStart = () => {
    startDragXRef.current = isOpen ? -maxReveal : 0;
    movedRef.current = false;
    suppressClickRef.current = false;
    setIsPressing(true);
    clearLongPressTimer();
    longPressTimerRef.current = window.setTimeout(() => {
      if (movedRef.current) return;
      suppressClickRef.current = true;
      setIsPressing(false);
      commitPosition(false);
      onLongPress(meal);
    }, MEAL_ACTION_LONG_PRESS_DELAY_MS);
  };

  const handleTouchEnd = useCallback(() => {
    clearLongPressTimer();
    setIsPressing(false);
    if (suppressClickRef.current) {
      movedRef.current = false;
      return;
    }
    const shouldOpen = dragXRef.current <= -46;
    commitPosition(shouldOpen);
  }, [clearLongPressTimer, commitPosition]);

  const handleRowClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    if (movedRef.current) {
      movedRef.current = false;
      return;
    }
    if (isOpen) {
      commitPosition(false);
      return;
    }
    onOpen(meal);
  };

  const handleTouchCancel = () => {
    clearLongPressTimer();
    movedRef.current = false;
    setIsPressing(false);
  };

  const swipeHandlers = useHorizontalSwipeGesture<HTMLButtonElement>({
    enabled: !isDeleting,
    onStart: handleTouchStart,
    onHorizontalLock: () => {
      movedRef.current = true;
      setIsPressing(false);
      clearLongPressTimer();
    },
    onVerticalLock: () => {
      movedRef.current = true;
      setIsPressing(false);
      clearLongPressTimer();
    },
    onHorizontalMove: ({ deltaX }) => {
      const next = Math.max(-maxReveal, Math.min(0, startDragXRef.current + deltaX));
      dragXRef.current = next;
      setDragX(next);
    },
    onHorizontalEnd: () => {
      handleTouchEnd();
    },
    onCancel: handleTouchCancel,
  });
  const { onTouchEnd: handleSwipeTouchEnd, ...rowTouchHandlers } = swipeHandlers;

  const handleAnyTouchEnd = useCallback((event: React.TouchEvent<HTMLButtonElement>) => {
    handleSwipeTouchEnd?.(event);
    clearLongPressTimer();
    setIsPressing(false);
  }, [clearLongPressTimer, handleSwipeTouchEnd]);

  return (
    <div className="relative overflow-hidden rounded-[22px]">
      <div className="absolute inset-y-0 right-0 flex w-[94px] items-center justify-center rounded-[22px] bg-[linear-gradient(135deg,#ea6868_0%,#d94b4b_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.16)] transition-opacity duration-150" style={{ opacity: isOpen || dragX < -2 ? 1 : 0 }}>
        <button
          type="button"
          onClick={() => void onDelete(meal)}
          disabled={isDeleting}
          className="flex h-full w-full items-center justify-center gap-2 text-[13px] font-black tracking-[-0.03em] text-white disabled:opacity-60"
        >
          {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          <span>Удалить</span>
        </button>
      </div>

      <button
        type="button"
        onClick={handleRowClick}
        {...rowTouchHandlers}
        onTouchEnd={handleAnyTouchEnd}
        onContextMenu={(event) => {
          event.preventDefault();
          setIsPressing(false);
          commitPosition(false);
          onLongPress(meal);
        }}
        className={`noma-v2-meal-row relative z-[1] select-none transition-[transform,filter] duration-150 ease-out ${isPressing ? 'brightness-[0.98]' : ''}`}
        style={{
          ...TOUCH_HOLD_PROTECTION_STYLE,
          touchAction: 'pan-y',
          transform: `translateX(${dragX}px) scale(${isPressing ? 0.985 : 1})`,
        }}
      >
        <div className="noma-v2-meal-thumb">
          <AuthorizedMealImage
            src={getMealPhotoUrl(meal)}
            alt={meal.name}
            className="h-full w-full object-cover"
            fallback={<MealVisualPlaceholder kind={getMealVisualKind(meal)} />}
          />
        </div>

        <div className="min-w-0 flex-1 text-left pt-0.5">
          <div className="truncate text-[15px] font-semibold tracking-[-0.025em] text-[#111113]">{meal.name}</div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] font-semibold text-[#767682]">
            <span>Б {roundNumber(meal.protein)}</span>
            <span>Ж {roundNumber(meal.fats)}</span>
            <span>У {roundNumber(meal.carbs)}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] font-medium text-[#7b7b87]">
            <span>{formatShortTime(meal.createdAt)}</span>
            {hasPortionAdjustment ? (
              <span className="inline-flex items-center rounded-full bg-[rgba(127,84,255,0.08)] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#7f54ff]">
                Съедено {consumedPercent}%
              </span>
            ) : null}
          </div>
        </div>

        <div className="min-w-[84px] pr-9 pt-1 text-right">
          <div className="text-[19px] font-bold tracking-[-0.04em] text-[#111113]">{roundNumber(meal.calories)}</div>
          <div className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#8c8c98]">ккал</div>
        </div>
      </button>
      <button
        type="button"
        aria-label={`Действия с приёмом «${meal.name}»`}
        onClick={() => {
          clearLongPressTimer();
          setIsPressing(false);
          commitPosition(false);
          onLongPress(meal);
        }}
        className={`absolute right-2 top-1/2 z-[2] flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-[12px] text-[#8b63ff] transition-opacity ${themeMode === 'dark' ? 'bg-[rgba(139,99,255,0.16)]' : 'bg-[#f3efff]'} ${isOpen || dragX < -2 ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
    </div>
  );
}

export function SwipeableActivityRow({
  activity,
  onEdit,
  onDelete,
  isDeleting,
  themeMode,
}: {
  activity: MiniAppActivity;
  onEdit: (activity: MiniAppActivity) => void;
  onDelete: (activity: MiniAppActivity) => void;
  isDeleting: boolean;
  themeMode: 'light' | 'dark';
}) {
  const [dragX, setDragX] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const dragXRef = useRef(0);
  const startDragXRef = useRef(0);
  const movedRef = useRef(false);
  const maxReveal = 88;
  const meta = ACTIVITY_TYPE_META[activity.type] || ACTIVITY_TYPE_META.other;
  const Icon = meta.icon;
  const headingClass = themeMode === 'dark' ? 'text-white' : 'text-[#111113]';
  const mutedClass = themeMode === 'dark' ? 'text-[#a5a8b8]' : 'text-[rgba(32,32,32,0.74)]';
  const eyebrowClass = themeMode === 'dark' ? 'text-[#85899a]' : 'text-[rgba(32,32,32,0.58)]';

  const commitPosition = useCallback((nextOpen: boolean) => {
    setIsOpen(nextOpen);
    const nextX = nextOpen ? -maxReveal : 0;
    dragXRef.current = nextX;
    setDragX(nextX);
  }, []);

  const handleTouchStart = () => {
    startDragXRef.current = isOpen ? -maxReveal : 0;
    movedRef.current = false;
  };

  const handleTouchEnd = useCallback(() => {
    const shouldOpen = dragXRef.current <= -42;
    commitPosition(shouldOpen);
  }, [commitPosition]);

  const handleRowClick = () => {
    if (movedRef.current) {
      movedRef.current = false;
      return;
    }
    if (isOpen) {
      commitPosition(false);
      return;
    }
    onEdit(activity);
  };

  const secondaryLine = activity.name && activity.name !== meta.label ? meta.label : `${formatDurationMinutes(activity.durationMinutes)} • ${ACTIVITY_INTENSITY_LABELS[activity.intensity]}`;
  const tertiaryLine = activity.name && activity.name !== meta.label ? `${formatDurationMinutes(activity.durationMinutes)} • ${ACTIVITY_INTENSITY_LABELS[activity.intensity]}` : formatLongDate(activity.recordedAt);

  const swipeHandlers = useHorizontalSwipeGesture<HTMLButtonElement>({
    enabled: !isDeleting,
    onStart: handleTouchStart,
    onHorizontalLock: () => {
      movedRef.current = true;
    },
    onVerticalLock: () => {
      movedRef.current = true;
    },
    onHorizontalMove: ({ deltaX }) => {
      const next = Math.max(-maxReveal, Math.min(0, startDragXRef.current + deltaX));
      dragXRef.current = next;
      setDragX(next);
    },
    onHorizontalEnd: () => {
      handleTouchEnd();
    },
    onCancel: () => {
      movedRef.current = false;
    },
  });

  return (
    <div className="relative overflow-hidden rounded-[22px]">
      <div className="absolute inset-y-0 right-0 flex w-[88px] items-center justify-center rounded-[22px] bg-[linear-gradient(135deg,#ea6868_0%,#d94b4b_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.16)] transition-opacity duration-150" style={{ opacity: isOpen || dragX < -2 ? 1 : 0 }}>
        <button
          type="button"
          onClick={() => void onDelete(activity)}
          disabled={isDeleting}
          className="flex h-full w-full items-center justify-center gap-2 text-[13px] font-black tracking-[-0.03em] text-white disabled:opacity-60"
        >
          {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          <span>Удалить</span>
        </button>
      </div>

      <button
        type="button"
        onClick={handleRowClick}
        {...swipeHandlers}
        className={`relative z-[1] flex w-full items-center gap-4 rounded-[22px] p-4 text-left transition-transform duration-200 ease-out ${themeMode === 'dark' ? 'bg-[#171a22]' : 'bg-white'}`}
        style={{ transform: `translateX(${dragX}px)`, touchAction: 'pan-y' }}
      >
        <div className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-[22px] ${themeMode === 'dark' ? 'border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.04)]' : 'bg-[#f7f7fb]'}`}>
          <div className="flex h-11 w-11 items-center justify-center rounded-[16px]" style={{ backgroundColor: `${meta.color}18` }}>
            <Icon className="h-5 w-5" style={{ color: meta.color }} />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className={`truncate text-[18px] font-black tracking-[-0.04em] ${headingClass}`}>{activity.name || meta.label}</div>
          <div className={`mt-1 text-[14px] font-medium ${mutedClass}`}>{secondaryLine}</div>
          <div className={`mt-1 text-[12px] font-medium ${eyebrowClass}`}>{tertiaryLine}</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <div className="text-right">
            <div className={`text-[18px] font-black tracking-[-0.04em] ${headingClass}`}>{activity.caloriesBurned}</div>
            <div className="mt-1 text-[12px] font-black uppercase tracking-[0.12em] text-[#26c497]">ккал</div>
          </div>
          <div className={`flex h-9 w-9 items-center justify-center rounded-[12px] ${themeMode === 'dark' ? 'bg-[rgba(255,255,255,0.05)]' : 'bg-[#f6f6fb]'}`}>
            <Edit3 className="h-3.5 w-3.5 text-[#8b63ff]" />
          </div>
        </div>
      </button>
    </div>
  );
}

export function DeleteMealConfirmSheet({
  meal,
  isDeleting,
  onCancel,
  onConfirm,
  themeMode,
}: {
  meal: V2Meal | null;
  isDeleting: boolean;
  onCancel: () => void;
  onConfirm: (meal: V2Meal) => Promise<void>;
  themeMode: 'light' | 'dark';
}) {
  if (!meal) return null;

  return (
    <div className="noma-v2-modal-backdrop fixed inset-0 z-[70] flex items-end justify-center bg-[rgba(17,17,19,0.34)] px-4 pb-4 pt-10">
      <button type="button" aria-label="Закрыть" className="absolute inset-0 cursor-default" onClick={onCancel} />
      <div className={`noma-v2-sheet-enter noma-v2-sheet-enter--bottom relative w-full max-w-[360px] rounded-[30px] p-4 backdrop-blur-[12px] ${
        themeMode === 'dark'
          ? 'bg-[rgba(24,26,33,0.96)] shadow-[0_28px_60px_rgba(0,0,0,0.34)]'
          : 'bg-[rgba(255,255,255,0.96)] shadow-[0_28px_60px_rgba(24,20,43,0.22)]'
      }`}>
        <div className={`mx-auto h-1.5 w-12 rounded-full ${themeMode === 'dark' ? 'bg-[rgba(255,255,255,0.08)]' : 'bg-[rgba(17,17,19,0.08)]'}`} />
        <div className={`mt-4 text-[24px] font-black tracking-[-0.05em] ${themeMode === 'dark' ? 'text-white' : 'text-[#111113]'}`}>Удалить прием?</div>
        <p className={`mt-2 text-[14px] font-semibold leading-6 ${themeMode === 'dark' ? 'text-[#a5a8b8]' : 'text-[#767682]'}`}>
          <span className={`font-bold ${themeMode === 'dark' ? 'text-white' : 'text-[#111113]'}`}>{meal.name}</span> исчезнет из дневника и статистики за этот день.
        </p>
        <div className="mt-5 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className={`min-h-[52px] rounded-[18px] px-4 text-[14px] font-extrabold tracking-[-0.03em] disabled:opacity-60 ${
              themeMode === 'dark' ? 'bg-[#1f222b] text-white' : 'bg-[#f3f3f7] text-[#111113]'
            }`}
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => void onConfirm(meal)}
            disabled={isDeleting}
            className="flex min-h-[52px] items-center justify-center gap-2 rounded-[18px] bg-[linear-gradient(135deg,#ea6868_0%,#d94b4b_100%)] px-4 text-[14px] font-extrabold tracking-[-0.03em] text-white shadow-[0_14px_28px_rgba(217,75,75,0.24)] disabled:opacity-60"
          >
            {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            <span>Удалить</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export function MealActionsSheet({
  meal,
  themeMode,
  onClose,
  onAdjustPortion,
  onDelete,
}: {
  meal: V2Meal | null;
  themeMode: 'light' | 'dark';
  onClose: () => void;
  onAdjustPortion: (meal: V2Meal) => void;
  onDelete: (meal: V2Meal) => void;
}) {
  if (!meal) return null;

  const consumedPercent = getMealConsumedPercent(meal);

  return (
    <div className="noma-v2-modal-backdrop fixed inset-0 z-[72] flex items-end justify-center bg-[rgba(17,17,19,0.34)] px-4 pb-4 pt-10">
      <button type="button" aria-label="Закрыть" className="absolute inset-0 cursor-default" onClick={onClose} />
      <div
        className={`noma-v2-sheet-enter noma-v2-sheet-enter--bottom relative w-full max-w-[360px] rounded-[30px] p-4 backdrop-blur-[12px] ${
        themeMode === 'dark'
          ? 'bg-[rgba(24,26,33,0.96)] shadow-[0_28px_60px_rgba(0,0,0,0.34)]'
          : 'bg-[rgba(255,255,255,0.96)] shadow-[0_28px_60px_rgba(24,20,43,0.22)]'
      }`}
        style={TOUCH_HOLD_PROTECTION_STYLE}
      >
        <div className={`mx-auto h-1.5 w-12 rounded-full ${themeMode === 'dark' ? 'bg-[rgba(255,255,255,0.08)]' : 'bg-[rgba(17,17,19,0.08)]'}`} />
        <div className={`mt-4 text-[22px] font-black tracking-[-0.05em] ${themeMode === 'dark' ? 'text-white' : 'text-[#111113]'}`}>{meal.name}</div>
        <p className={`mt-2 text-[14px] font-semibold leading-6 ${themeMode === 'dark' ? 'text-[#a5a8b8]' : 'text-[#767682]'}`}>
          Выбери, что сделать с этим приёмом.
        </p>

        <div className="mt-5 space-y-3">
          <button
            type="button"
            onClick={() => onAdjustPortion(meal)}
            className={`flex min-h-[72px] w-full items-center gap-3 rounded-[22px] px-4 text-left ${
              themeMode === 'dark'
                ? 'bg-[rgba(139,99,255,0.16)] text-white ring-1 ring-[rgba(139,99,255,0.18)]'
                : 'bg-[rgba(127,84,255,0.08)] text-[#111113] ring-1 ring-[rgba(127,84,255,0.1)]'
            }`}
          >
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-black tracking-[-0.04em]">Осталось на тарелке</div>
              <div className={`mt-1 truncate text-[12px] font-semibold ${themeMode === 'dark' ? 'text-[#c7cce0]' : 'text-[#7f54ff]'}`}>
                Учтено {consumedPercent}% порции
              </div>
            </div>
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-[16px] ${themeMode === 'dark' ? 'bg-[rgba(255,255,255,0.06)]' : 'bg-[rgba(127,84,255,0.12)]'}`}>
              <UtensilsCrossed className="h-5 w-5 text-[#7f54ff]" />
            </div>
          </button>

          <button
            type="button"
            onClick={() => onDelete(meal)}
            className="flex min-h-[72px] w-full items-center gap-3 rounded-[22px] bg-[linear-gradient(135deg,#ea6868_0%,#d94b4b_100%)] px-4 text-left text-white shadow-[0_14px_28px_rgba(217,75,75,0.24)]"
          >
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-black tracking-[-0.04em]">Удалить</div>
              <div className="mt-1 truncate text-[12px] font-semibold text-white/80">Убрать из дневника</div>
            </div>
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[16px] bg-white/10">
              <Trash2 className="h-5 w-5" />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

export function AddMealChoiceSheet({
  themeMode,
  onClose,
  onChoosePhoto,
  onChooseText,
  onRepeatMeal,
}: {
  themeMode: 'light' | 'dark';
  onClose: () => void;
  onChoosePhoto: () => void;
  onChooseText: () => void;
  onRepeatMeal?: (item: FrequentMealItem) => Promise<void>;
}) {
  const api = useApi();
  const [frequentMeals, setFrequentMeals] = useState<FrequentMealItem[]>([]);
  const [repeatingKey, setRepeatingKey] = useState<string | null>(null);

  useEffect(() => {
    if (!onRepeatMeal) return;
    let cancelled = false;
    api.getFrequentMealsMiniApp(4)
      .then((items) => {
        if (!cancelled) setFrequentMeals(items);
      })
      .catch(() => {
        // блок «Повторить» просто не показываем
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRepeat = async (item: FrequentMealItem) => {
    if (!onRepeatMeal || repeatingKey) return;
    const key = `${item.category}:${item.name}`;
    try {
      setRepeatingKey(key);
      await onRepeatMeal(item);
    } finally {
      setRepeatingKey(null);
    }
  };

  return (
    <div className="noma-v2-modal-backdrop fixed inset-0 z-[72] flex items-end justify-center bg-[rgba(17,17,19,0.34)] px-4 pb-4 pt-10">
      <button type="button" aria-label="Закрыть" className="absolute inset-0 cursor-default" onClick={onClose} />
      <div
        className={`noma-v2-sheet-enter noma-v2-sheet-enter--bottom relative w-full max-w-[360px] rounded-[30px] p-4 backdrop-blur-[12px] ${
        themeMode === 'dark'
          ? 'bg-[rgba(24,26,33,0.96)] shadow-[0_28px_60px_rgba(0,0,0,0.34)]'
          : 'bg-[rgba(255,255,255,0.96)] shadow-[0_28px_60px_rgba(24,20,43,0.22)]'
      }`}
      >
        <div className={`mx-auto h-1.5 w-12 rounded-full ${themeMode === 'dark' ? 'bg-[rgba(255,255,255,0.08)]' : 'bg-[rgba(17,17,19,0.08)]'}`} />
        <div className={`mt-4 text-[22px] font-black tracking-[-0.05em] ${themeMode === 'dark' ? 'text-white' : 'text-[#111113]'}`}>Добавить приём пищи</div>
        <p className={`mt-2 text-[14px] font-semibold leading-6 ${themeMode === 'dark' ? 'text-[#a5a8b8]' : 'text-[#767682]'}`}>
          Выбери, как распознать еду.
        </p>

        <div className="mt-5 space-y-3">
          <button
            type="button"
            onClick={onChoosePhoto}
            className="flex min-h-[72px] w-full items-center gap-3 rounded-[22px] bg-[linear-gradient(135deg,#7f54ff_0%,#9f84ff_100%)] px-4 text-left text-white shadow-[0_14px_28px_rgba(127,84,255,0.28)]"
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[16px] bg-white/15">
              <Camera className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-black tracking-[-0.04em]">Сфотографировать</div>
              <div className="mt-1 truncate text-[12px] font-semibold text-white/80">ИИ распознает блюдо по фото</div>
            </div>
          </button>

          <button
            type="button"
            onClick={onChooseText}
            className={`flex min-h-[72px] w-full items-center gap-3 rounded-[22px] px-4 text-left ${
              themeMode === 'dark'
                ? 'bg-[rgba(139,99,255,0.16)] text-white ring-1 ring-[rgba(139,99,255,0.18)]'
                : 'bg-[rgba(127,84,255,0.08)] text-[#111113] ring-1 ring-[rgba(127,84,255,0.1)]'
            }`}
          >
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-[16px] ${themeMode === 'dark' ? 'bg-[rgba(255,255,255,0.06)]' : 'bg-[rgba(127,84,255,0.12)]'}`}>
              <FileText className="h-5 w-5 text-[#7f54ff]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-black tracking-[-0.04em]">Описать текстом</div>
              <div className={`mt-1 truncate text-[12px] font-semibold ${themeMode === 'dark' ? 'text-[#c7cce0]' : 'text-[#7f54ff]'}`}>
                Напиши, что съел — ИИ посчитает КБЖУ
              </div>
            </div>
          </button>
        </div>

        {frequentMeals.length > 0 ? (
          <div className="mt-5">
            <div className={`text-[12px] font-black uppercase tracking-[0.16em] ${themeMode === 'dark' ? 'text-[#85899a]' : 'text-[rgba(32,32,32,0.58)]'}`}>
              Повторить недавнее
            </div>
            <div className="mt-3 space-y-2">
              {frequentMeals.map((item) => {
                const key = `${item.category}:${item.name}`;
                const isRepeating = repeatingKey === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => void handleRepeat(item)}
                    disabled={repeatingKey !== null}
                    className={`flex w-full items-center gap-3 rounded-[18px] px-4 py-3 text-left ${
                      themeMode === 'dark' ? 'bg-[rgba(255,255,255,0.05)]' : 'bg-[#f7f7fb]'
                    } ${repeatingKey !== null && !isRepeating ? 'opacity-50' : ''}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className={`truncate text-[14px] font-extrabold tracking-[-0.03em] ${themeMode === 'dark' ? 'text-white' : 'text-[#111113]'}`}>
                        {item.name}
                      </div>
                      <div className={`mt-0.5 text-[12px] font-semibold ${themeMode === 'dark' ? 'text-[#a5a8b8]' : 'text-[#767682]'}`}>
                        {getMealTypeLabel(item.category)} · {item.calories} ккал
                      </div>
                    </div>
                    {isRepeating ? (
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[#7f54ff]" />
                    ) : (
                      <Plus className="h-4 w-4 shrink-0 text-[#7f54ff]" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export type AnalysisIngredientPath =
  | { kind: 'item'; index: number }
  | { kind: 'dishItem'; dishIndex: number; itemIndex: number };

export function IngredientEditRow({
  item,
  themeMode,
  busy,
  onChangeWeight,
  onDelete,
  onClarify,
}: {
  item: any;
  themeMode: 'light' | 'dark';
  busy: boolean;
  onChangeWeight: (grams: number) => void;
  onDelete: () => void;
  onClarify: () => void;
}) {
  const [weightDraft, setWeightDraft] = useState(String(Math.round(Number(item?.weight) || 0)));

  useEffect(() => {
    setWeightDraft(String(Math.round(Number(item?.weight) || 0)));
  }, [item?.weight]);

  return (
    <div className={`flex items-center gap-2 rounded-[16px] px-3 py-2.5 ${themeMode === 'dark' ? 'bg-[#1f222b]' : 'bg-[#f7f7fb]'}`}>
      <div className="min-w-0 flex-1">
        <div className={`truncate text-[13px] font-bold ${themeMode === 'dark' ? 'text-white' : 'text-[#111113]'}`}>{item?.displayName || item?.foodName}</div>
        <div className="text-[11px] font-semibold text-[#9a9aa6]">{roundNumber(item?.calories)} ккал</div>
      </div>
      <input
        type="number"
        inputMode="numeric"
        value={weightDraft}
        onChange={(e) => setWeightDraft(e.target.value)}
        onBlur={() => {
          const grams = Math.round(Number(weightDraft));
          if (Number.isFinite(grams) && grams > 0 && grams !== Math.round(Number(item?.weight) || 0)) onChangeWeight(grams);
        }}
        disabled={busy}
        className={`w-16 rounded-[10px] px-2 py-1.5 text-center text-[13px] font-bold outline-none ${themeMode === 'dark' ? 'bg-[#111319] text-white' : 'bg-white text-[#111113]'}`}
      />
      <span className="text-[11px] font-semibold text-[#9a9aa6]">г</span>
      <button type="button" onClick={onClarify} disabled={busy} className="flex h-8 w-8 shrink-0 items-center justify-center text-[#7f54ff]" aria-label="Уточнить">
        <Sparkles className="h-4 w-4" />
      </button>
      <button type="button" onClick={onDelete} disabled={busy} className="flex h-8 w-8 shrink-0 items-center justify-center text-[#e25c5c]" aria-label="Удалить">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
      </button>
    </div>
  );
}

export function AddMealTextScreen({
  onBack,
  themeMode,
  onSaved,
}: {
  onBack: () => void;
  themeMode: 'light' | 'dark';
  onSaved: () => void;
}) {
  const api = useApi();
  const ensureMiniAppAuth = useMiniAppAuth();
  const { requestBack } = useMiniAppBackNavigation('add-meal-text', onBack);
  const swipeBackHandlers = useEdgeSwipeBack(true, requestBack);

  const [step, setStep] = useState<'input' | 'review'>('input');
  const [text, setText] = useState('');
  const [category, setCategory] = useState<'breakfast' | 'lunch' | 'dinner' | 'snack'>('breakfast');
  const [analysis, setAnalysis] = useState<any | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [refineTarget, setRefineTarget] = useState<{ scope: 'whole_meal' | 'selected_dish' | 'selected_item'; dishIndex?: number; path?: AnalysisIngredientPath; label: string } | null>(null);
  const [refineText, setRefineText] = useState('');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingStartedAtRef = useRef(0);
  const recordingTimerRef = useRef<number | null>(null);
  const cancelRecordingRef = useRef(false);

  const headingClass = themeMode === 'dark' ? 'text-white' : 'text-[#111113]';
  const mutedClass = themeMode === 'dark' ? 'text-[#a5a8b8]' : 'text-[#767682]';
  const cardShellClass = themeMode === 'dark' ? 'bg-[#181a21]' : 'bg-white';

  const analyzeText = async (value: string) => {
    const trimmed = value.trim();
    if (trimmed.length < 2) {
      setError('Напиши, что ты съел.');
      return;
    }
    setIsAnalyzing(true);
    setError(null);
    try {
      await ensureMiniAppAuth();
      const result = await api.analyzeMealTextMiniApp(trimmed);
      if (!result.success) {
        setError(result.error === 'not_food' ? 'Это не похоже на еду. Попробуй описать иначе.' : (result.message || 'Не удалось проанализировать.'));
        return;
      }
      setAnalysis(result.analysis);
      setStep('review');
    } catch (e: any) {
      setError(e?.message || 'Не удалось проанализировать.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleAnalyze = async () => analyzeText(text);

  const releaseMicrophone = useCallback(() => {
    if (recordingTimerRef.current !== null) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
    mediaRecorderRef.current = null;
    setIsRecording(false);
  }, []);

  useEffect(() => () => {
    cancelRecordingRef.current = true;
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    releaseMicrophone();
  }, [releaseMicrophone]);

  const startVoiceRecording = async () => {
    if (isAnalyzing || isTranscribing || isRecording) return;
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Запись голоса не поддерживается на этом устройстве. Можно описать еду текстом.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferredTypes = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm'];
      const mimeType = preferredTypes.find((type) => MediaRecorder.isTypeSupported(type));
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      cancelRecordingRef.current = false;
      recordingStartedAtRef.current = Date.now();
      setRecordingSeconds(0);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };
      recorder.onstop = async () => {
        const durationSeconds = Math.max(1, Math.round((Date.now() - recordingStartedAtRef.current) / 1000));
        const chunks = audioChunksRef.current;
        const wasCancelled = cancelRecordingRef.current;
        const recordedType = recorder.mimeType || mimeType || 'audio/webm';
        releaseMicrophone();
        if (wasCancelled || chunks.length === 0) return;

        setIsTranscribing(true);
        try {
          await ensureMiniAppAuth();
          const canonicalType = recordedType.includes('mp4') ? 'audio/mp4' : 'audio/webm';
          const extension = canonicalType === 'audio/mp4' ? 'm4a' : 'webm';
          const file = new File([new Blob(chunks, { type: canonicalType })], `meal-voice.${extension}`, { type: canonicalType });
          const result = await api.transcribeMealAudioMiniApp(file, durationSeconds);
          if (!result.success) {
            setError(result.error === 'empty_transcription' ? 'Не получилось разобрать запись. Попробуй сказать ещё раз.' : 'Не получилось обработать голосовое. Попробуй ещё раз.');
            return;
          }
          if (result.text.length < 2) {
            setError('Не получилось разобрать запись. Попробуй сказать ещё раз.');
            return;
          }
          setText(result.text);
          await analyzeText(result.text);
        } catch (voiceError: any) {
          const permissionDenied = voiceError?.name === 'NotAllowedError' || voiceError?.name === 'PermissionDeniedError';
          setError(permissionDenied ? 'Разреши доступ к микрофону в настройках Telegram.' : (voiceError?.message || 'Не получилось обработать голосовое.'));
        } finally {
          setIsTranscribing(false);
        }
      };

      recorder.start(250);
      setIsRecording(true);
      recordingTimerRef.current = window.setInterval(() => {
        const elapsed = Math.floor((Date.now() - recordingStartedAtRef.current) / 1000);
        setRecordingSeconds(elapsed);
        if (elapsed >= 120 && recorder.state === 'recording') recorder.stop();
      }, 250);
    } catch (voiceError: any) {
      releaseMicrophone();
      const permissionDenied = voiceError?.name === 'NotAllowedError' || voiceError?.name === 'PermissionDeniedError';
      setError(permissionDenied ? 'Разреши доступ к микрофону в настройках Telegram.' : 'Не получилось включить микрофон.');
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
  };

  const cancelVoiceRecording = () => {
    cancelRecordingRef.current = true;
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    else releaseMicrophone();
  };

  const applyEdit = async (
    payload: { action: 'set_weight' | 'delete' | 'refine'; path?: AnalysisIngredientPath; weightGrams?: number; scope?: string; userText?: string; dishIndex?: number },
    busyId: string,
  ) => {
    if (!analysis) return;
    setBusyKey(busyId);
    setError(null);
    try {
      const result = await api.editMealAnalysisMiniApp({ ...(payload as any), analysis });
      if (!result.success) {
        setError(result.error === 'refine_failed' ? 'Не удалось обновить анализ. Попробуй переформулировать.' : 'Не удалось применить изменение.');
        return;
      }
      setAnalysis(result.analysis);
      setRefineTarget(null);
      setRefineText('');
    } catch (e: any) {
      setError(e?.message || 'Не удалось применить изменение.');
    } finally {
      setBusyKey(null);
    }
  };

  const handleSave = async () => {
    if (!analysis) return;
    setIsSaving(true);
    setError(null);
    try {
      await ensureMiniAppAuth();
      await api.saveMealAnalysisMiniApp({ analysis, category });
      onSaved();
      onBack();
    } catch (e: any) {
      setError(e?.message || 'Не удалось сохранить приём пищи.');
    } finally {
      setIsSaving(false);
    }
  };

  const dishes: any[] = Array.isArray(analysis?.dishes) && analysis.dishes.length > 0 ? analysis.dishes : (analysis ? [analysis] : []);
  const microRows = getMicronutrientRows(analysis?.micronutrients);

  return (
    <div {...swipeBackHandlers} className="noma-v2-screen pb-28 pt-4">
      <header className="flex items-center gap-3 px-4 pt-2">
        <button type="button" onClick={requestBack} className="noma-v2-circle-button">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className={`text-[20px] font-black tracking-[-0.04em] ${headingClass}`}>
          {step === 'input' ? 'Описать текстом' : 'Проверь разбор'}
        </div>
      </header>

      {step === 'input' ? (
        <section className="mt-5 px-4">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Например: гречка с курицей 200г и салат из огурцов с маслом"
            rows={5}
            className={`w-full rounded-[20px] p-4 text-[15px] font-medium outline-none ${themeMode === 'dark' ? 'bg-[#181a21] text-white placeholder:text-[#5b5e6b]' : 'bg-[#f3f3f7] text-[#111113] placeholder:text-[#a1a1ab]'}`}
          />

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={isRecording ? stopVoiceRecording : () => void startVoiceRecording()}
              disabled={isAnalyzing || isTranscribing}
              className={`flex min-h-[50px] flex-1 items-center justify-center gap-2 rounded-[17px] text-[14px] font-black transition-transform active:scale-[0.98] disabled:opacity-60 ${isRecording ? 'bg-[#ff5f67] text-white' : themeMode === 'dark' ? 'bg-[rgba(139,99,255,0.18)] text-white' : 'bg-[#eee9ff] text-[#7048e8]'}`}
            >
              {isTranscribing ? <Loader2 className="h-5 w-5 animate-spin" /> : isRecording ? <Square className="h-4 w-4 fill-current" /> : <Mic className="h-5 w-5" />}
              <span>{isTranscribing ? 'Распознаю…' : isRecording ? `Готово · ${Math.floor(recordingSeconds / 60)}:${String(recordingSeconds % 60).padStart(2, '0')}` : 'Сказать голосом'}</span>
            </button>
            {isRecording ? (
              <button type="button" onClick={cancelVoiceRecording} className={`min-h-[50px] rounded-[17px] px-4 text-[13px] font-black ${themeMode === 'dark' ? 'bg-[#181a21] text-[#a5a8b8]' : 'bg-[#f3f3f7] text-[#767682]'}`}>
                Отмена
              </button>
            ) : null}
          </div>

          <div className="mt-4 grid grid-cols-4 gap-2">
            {mealTypeDefinitions.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setCategory(item.id)}
                className={`rounded-[14px] py-2.5 text-[13px] font-bold ${category === item.id
                  ? 'bg-[linear-gradient(135deg,#7f54ff_0%,#9f84ff_100%)] text-white'
                  : themeMode === 'dark' ? 'bg-[#181a21] text-[#a5a8b8]' : 'bg-[#f3f3f7] text-[#767682]'}`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {error ? <div className="mt-4 text-[13px] font-semibold text-[#e25c5c]">{error}</div> : null}

          <button
            type="button"
            onClick={() => void handleAnalyze()}
            disabled={isAnalyzing || text.trim().length < 2}
            className="noma-v2-primary-button mt-5 flex min-h-[54px] w-full items-center justify-center gap-2 rounded-[20px] disabled:opacity-60"
          >
            {isAnalyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            <span>Анализировать</span>
          </button>
        </section>
      ) : (
        <section className="mt-5 space-y-4 px-4">
          <article className={`noma-v2-card p-4 ${cardShellClass}`}>
            <div className="flex items-center justify-between gap-3">
              <div className={`min-w-0 truncate text-[17px] font-black tracking-[-0.04em] ${headingClass}`}>{analysis?.displayName || analysis?.foodName}</div>
              <div className={`shrink-0 text-[20px] font-black ${headingClass}`}>{roundNumber(analysis?.calories)} ккал</div>
            </div>
            <div className={`mt-2 text-[13px] font-semibold ${mutedClass}`}>
              Б:{roundNumber(analysis?.protein)}г Ж:{roundNumber(analysis?.fats)}г У:{roundNumber(analysis?.carbs)}г Кл:{typeof analysis?.fiber === 'number' ? roundNumber(analysis.fiber) : '—'}г · {roundNumber(analysis?.weight)}г
            </div>

            {microRows.length > 0 ? (
              <div className="mt-3 grid grid-cols-3 gap-2">
                {microRows.map((row) => (
                  <div key={row.key} className="flex items-center gap-1.5 rounded-[12px] bg-[#f7f7fb] px-2 py-1.5">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full" style={{ background: `${row.color}22` }}>{row.icon}</span>
                    <div className="min-w-0 leading-tight">
                      <div className="truncate text-[12px] font-extrabold text-[#111113]">{row.value}<span className="text-[9px] font-bold text-[#a1a1ab]"> {row.unit}</span></div>
                      <div className="truncate text-[9px] font-semibold text-[#9a9aa6]">{row.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            <button
              type="button"
              onClick={() => setRefineTarget({ scope: 'whole_meal', label: 'весь приём' })}
              disabled={busyKey === 'refine'}
              className={`mt-3 flex w-full items-center justify-center gap-2 rounded-[14px] py-2.5 text-[13px] font-bold ${themeMode === 'dark' ? 'bg-[rgba(139,99,255,0.16)] text-white' : 'bg-[rgba(127,84,255,0.08)] text-[#7f54ff]'}`}
            >
              <Sparkles className="h-4 w-4" />
              Уточнить весь приём
            </button>
          </article>

          {dishes.map((dish, dishIndex) => {
            const items = Array.isArray(dish?.items) ? dish.items : [];
            const dishName = String(dish?.displayName || dish?.name || 'Блюдо');
            return (
              <article key={dishIndex} className={`noma-v2-card p-4 ${cardShellClass}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className={`min-w-0 truncate text-[15px] font-black tracking-[-0.04em] ${headingClass}`}>{dishName}</div>
                  <button
                    type="button"
                    onClick={() => setRefineTarget({ scope: 'selected_dish', dishIndex, label: dishName })}
                    disabled={busyKey === 'refine'}
                    className="flex h-8 w-8 shrink-0 items-center justify-center text-[#7f54ff]"
                    aria-label="Уточнить блюдо"
                  >
                    <Sparkles className="h-4 w-4" />
                  </button>
                </div>
                <div className={`mt-1 text-[12px] font-semibold ${mutedClass}`}>
                  {roundNumber(dish?.calories)} ккал · {roundNumber(dish?.weight)}г
                </div>

                {items.length > 0 ? (
                  <div className="mt-3 space-y-2">
                    {items.map((item: any, itemIndex: number) => {
                      const path: AnalysisIngredientPath = { kind: 'dishItem', dishIndex, itemIndex };
                      const rowKey = `dishItem:${dishIndex}:${itemIndex}`;
                      const itemName = String(item?.displayName || item?.foodName || 'Ингредиент');
                      return (
                        <IngredientEditRow
                          key={rowKey}
                          item={item}
                          themeMode={themeMode}
                          busy={busyKey === rowKey}
                          onChangeWeight={(grams) => void applyEdit({ action: 'set_weight', path, weightGrams: grams }, rowKey)}
                          onDelete={() => void applyEdit({ action: 'delete', path }, rowKey)}
                          onClarify={() => setRefineTarget({ scope: 'selected_item', path, label: itemName })}
                        />
                      );
                    })}
                  </div>
                ) : null}
              </article>
            );
          })}

          {refineTarget ? (
            <article className={`noma-v2-card p-4 ${cardShellClass}`}>
              <div className={`text-[13px] font-bold ${headingClass}`}>Уточнение: {refineTarget.label}</div>
              <input
                type="text"
                autoFocus
                value={refineText}
                onChange={(e) => setRefineText(e.target.value)}
                placeholder="Например: это сырники, а не оладьи"
                className={`mt-2 w-full rounded-[14px] px-4 py-3 text-[14px] font-medium outline-none ${themeMode === 'dark' ? 'bg-[#111319] text-white placeholder:text-[#5b5e6b]' : 'bg-[#f3f3f7] text-[#111113] placeholder:text-[#a1a1ab]'}`}
              />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => { setRefineTarget(null); setRefineText(''); }}
                  className={`min-h-[46px] rounded-[14px] text-[13px] font-extrabold ${themeMode === 'dark' ? 'bg-[#1f222b] text-white' : 'bg-[#f3f3f7] text-[#111113]'}`}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={() => void applyEdit({
                    action: 'refine',
                    scope: refineTarget.scope,
                    userText: refineText.trim(),
                    dishIndex: refineTarget.dishIndex,
                    path: refineTarget.path,
                  }, 'refine')}
                  disabled={refineText.trim().length < 2 || busyKey === 'refine'}
                  className="noma-v2-primary-button flex min-h-[46px] items-center justify-center gap-2 rounded-[14px] text-[13px] disabled:opacity-60"
                >
                  {busyKey === 'refine' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Отправить
                </button>
              </div>
            </article>
          ) : null}

          {error ? <div className="text-[13px] font-semibold text-[#e25c5c]">{error}</div> : null}

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setStep('input')}
              className={`min-h-[52px] rounded-[18px] text-[14px] font-extrabold ${themeMode === 'dark' ? 'bg-[#1f222b] text-white' : 'bg-[#f3f3f7] text-[#111113]'}`}
            >
              Изменить текст
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={isSaving}
              className="noma-v2-primary-button flex min-h-[52px] items-center justify-center gap-2 rounded-[18px] disabled:opacity-60"
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Сохранить
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

export function AddMealPhotoScreen({
  onBack,
  themeMode,
  onSaved,
}: {
  onBack: () => void;
  themeMode: 'light' | 'dark';
  onSaved: () => void;
}) {
  const api = useApi();
  const ensureMiniAppAuth = useMiniAppAuth();
  const { requestBack } = useMiniAppBackNavigation('add-meal-photo', onBack);
  const swipeBackHandlers = useEdgeSwipeBack(true, requestBack);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<'capture' | 'review'>('capture');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [hint, setHint] = useState('');
  const [category, setCategory] = useState<'breakfast' | 'lunch' | 'dinner' | 'snack'>('breakfast');
  const [analysis, setAnalysis] = useState<any | null>(null);
  const [photoToken, setPhotoToken] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [refineTarget, setRefineTarget] = useState<{ scope: 'whole_meal' | 'selected_dish' | 'selected_item'; dishIndex?: number; path?: AnalysisIngredientPath; label: string } | null>(null);
  const [refineText, setRefineText] = useState('');

  const headingClass = themeMode === 'dark' ? 'text-white' : 'text-[#111113]';
  const mutedClass = themeMode === 'dark' ? 'text-[#a5a8b8]' : 'text-[#767682]';
  const cardShellClass = themeMode === 'dark' ? 'bg-[#181a21]' : 'bg-white';

  const resetToCapture = () => {
    setStep('capture');
    setFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setHint('');
    setAnalysis(null);
    setPhotoToken(null);
    setError(null);
  };

  const handlePickFile = (picked: File | null | undefined) => {
    if (!picked) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(picked);
    setPreviewUrl(URL.createObjectURL(picked));
    setError(null);
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setIsAnalyzing(true);
    setError(null);
    try {
      await ensureMiniAppAuth();
      const result = await api.analyzeMealPhotoMiniApp(file, hint.trim() || undefined);
      if (!result.success) {
        setError(result.error === 'not_food_photo' ? 'На фото не похоже на еду. Попробуй другое фото.' : (result.message || 'Не удалось проанализировать фото.'));
        return;
      }
      setAnalysis(result.analysis);
      setPhotoToken(result.photoToken);
      setStep('review');
    } catch (e: any) {
      setError(e?.message || 'Не удалось проанализировать фото.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const applyEdit = async (
    payload: { action: 'set_weight' | 'delete' | 'refine'; path?: AnalysisIngredientPath; weightGrams?: number; scope?: string; userText?: string; dishIndex?: number },
    busyId: string,
  ) => {
    if (!analysis) return;
    setBusyKey(busyId);
    setError(null);
    try {
      const result = await api.editMealAnalysisMiniApp({ ...(payload as any), analysis });
      if (!result.success) {
        setError(result.error === 'refine_failed' ? 'Не удалось обновить анализ. Попробуй переформулировать.' : 'Не удалось применить изменение.');
        return;
      }
      setAnalysis(result.analysis);
      setRefineTarget(null);
      setRefineText('');
    } catch (e: any) {
      setError(e?.message || 'Не удалось применить изменение.');
    } finally {
      setBusyKey(null);
    }
  };

  const handleSave = async () => {
    if (!analysis) return;
    setIsSaving(true);
    setError(null);
    try {
      await ensureMiniAppAuth();
      await api.saveMealAnalysisMiniApp({ analysis, category, photoToken: photoToken || undefined });
      onSaved();
      onBack();
    } catch (e: any) {
      setError(e?.message || 'Не удалось сохранить приём пищи.');
    } finally {
      setIsSaving(false);
    }
  };

  const dishes: any[] = Array.isArray(analysis?.dishes) && analysis.dishes.length > 0 ? analysis.dishes : (analysis ? [analysis] : []);
  const microRows = getMicronutrientRows(analysis?.micronutrients);

  return (
    <div {...swipeBackHandlers} className="noma-v2-screen pb-28 pt-4">
      <header className="flex items-center gap-3 px-4 pt-2">
        <button type="button" onClick={requestBack} className="noma-v2-circle-button">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className={`text-[20px] font-black tracking-[-0.04em] ${headingClass}`}>
          {step === 'capture' ? 'Сфотографировать' : 'Проверь разбор'}
        </div>
      </header>

      {step === 'capture' ? (
        <section className="mt-5 px-4">
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => handlePickFile(e.target.files?.[0])}
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handlePickFile(e.target.files?.[0])}
          />

          {previewUrl ? (
            <button type="button" onClick={() => galleryInputRef.current?.click()} className="block w-full overflow-hidden rounded-[24px]">
              <img src={previewUrl} alt="Фото еды" className="h-[260px] w-full object-cover" />
            </button>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                className="flex h-[140px] flex-col items-center justify-center gap-2 rounded-[22px] bg-[linear-gradient(135deg,#7f54ff_0%,#9f84ff_100%)] text-white shadow-[0_14px_28px_rgba(127,84,255,0.28)]"
              >
                <Camera className="h-7 w-7" />
                <span className="text-[13px] font-bold">Камера</span>
              </button>
              <button
                type="button"
                onClick={() => galleryInputRef.current?.click()}
                className={`flex h-[140px] flex-col items-center justify-center gap-2 rounded-[22px] ${themeMode === 'dark' ? 'bg-[#181a21] text-white' : 'bg-[#f3f3f7] text-[#111113]'}`}
              >
                <FileText className="h-7 w-7 text-[#7f54ff]" />
                <span className="text-[13px] font-bold">Галерея</span>
              </button>
            </div>
          )}

          <input
            type="text"
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            placeholder="Необязательно: уточнение к фото (например, «без соуса»)"
            className={`mt-4 w-full rounded-[16px] px-4 py-3 text-[14px] font-medium outline-none ${themeMode === 'dark' ? 'bg-[#181a21] text-white placeholder:text-[#5b5e6b]' : 'bg-[#f3f3f7] text-[#111113] placeholder:text-[#a1a1ab]'}`}
          />

          <div className="mt-4 grid grid-cols-4 gap-2">
            {mealTypeDefinitions.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setCategory(item.id)}
                className={`rounded-[14px] py-2.5 text-[13px] font-bold ${category === item.id
                  ? 'bg-[linear-gradient(135deg,#7f54ff_0%,#9f84ff_100%)] text-white'
                  : themeMode === 'dark' ? 'bg-[#181a21] text-[#a5a8b8]' : 'bg-[#f3f3f7] text-[#767682]'}`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {error ? <div className="mt-4 text-[13px] font-semibold text-[#e25c5c]">{error}</div> : null}

          <button
            type="button"
            onClick={() => void handleAnalyze()}
            disabled={isAnalyzing || !file}
            className="noma-v2-primary-button mt-5 flex min-h-[54px] w-full items-center justify-center gap-2 rounded-[20px] disabled:opacity-60"
          >
            {isAnalyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            <span>Анализировать</span>
          </button>
        </section>
      ) : (
        <section className="mt-5 space-y-4 px-4">
          <article className={`noma-v2-card overflow-hidden p-0 ${cardShellClass}`}>
            {previewUrl ? <img src={previewUrl} alt="Фото еды" className="h-[160px] w-full object-cover" /> : null}
            <div className="p-4">
              <div className="flex items-center justify-between gap-3">
                <div className={`min-w-0 truncate text-[17px] font-black tracking-[-0.04em] ${headingClass}`}>{analysis?.displayName || analysis?.foodName}</div>
                <div className={`shrink-0 text-[20px] font-black ${headingClass}`}>{roundNumber(analysis?.calories)} ккал</div>
              </div>
              <div className={`mt-2 text-[13px] font-semibold ${mutedClass}`}>
                Б:{roundNumber(analysis?.protein)}г Ж:{roundNumber(analysis?.fats)}г У:{roundNumber(analysis?.carbs)}г Кл:{typeof analysis?.fiber === 'number' ? roundNumber(analysis.fiber) : '—'}г · {roundNumber(analysis?.weight)}г
              </div>

              {microRows.length > 0 ? (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {microRows.map((row) => (
                    <div key={row.key} className="flex items-center gap-1.5 rounded-[12px] bg-[#f7f7fb] px-2 py-1.5">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full" style={{ background: `${row.color}22` }}>{row.icon}</span>
                      <div className="min-w-0 leading-tight">
                        <div className="truncate text-[12px] font-extrabold text-[#111113]">{row.value}<span className="text-[9px] font-bold text-[#a1a1ab]"> {row.unit}</span></div>
                        <div className="truncate text-[9px] font-semibold text-[#9a9aa6]">{row.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              <button
                type="button"
                onClick={() => setRefineTarget({ scope: 'whole_meal', label: 'весь приём' })}
                disabled={busyKey === 'refine'}
                className={`mt-3 flex w-full items-center justify-center gap-2 rounded-[14px] py-2.5 text-[13px] font-bold ${themeMode === 'dark' ? 'bg-[rgba(139,99,255,0.16)] text-white' : 'bg-[rgba(127,84,255,0.08)] text-[#7f54ff]'}`}
              >
                <Sparkles className="h-4 w-4" />
                Уточнить весь приём
              </button>
            </div>
          </article>

          {dishes.map((dish, dishIndex) => {
            const items = Array.isArray(dish?.items) ? dish.items : [];
            const dishName = String(dish?.displayName || dish?.name || 'Блюдо');
            return (
              <article key={dishIndex} className={`noma-v2-card p-4 ${cardShellClass}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className={`min-w-0 truncate text-[15px] font-black tracking-[-0.04em] ${headingClass}`}>{dishName}</div>
                  <button
                    type="button"
                    onClick={() => setRefineTarget({ scope: 'selected_dish', dishIndex, label: dishName })}
                    disabled={busyKey === 'refine'}
                    className="flex h-8 w-8 shrink-0 items-center justify-center text-[#7f54ff]"
                    aria-label="Уточнить блюдо"
                  >
                    <Sparkles className="h-4 w-4" />
                  </button>
                </div>
                <div className={`mt-1 text-[12px] font-semibold ${mutedClass}`}>
                  {roundNumber(dish?.calories)} ккал · {roundNumber(dish?.weight)}г
                </div>

                {items.length > 0 ? (
                  <div className="mt-3 space-y-2">
                    {items.map((item: any, itemIndex: number) => {
                      const path: AnalysisIngredientPath = { kind: 'dishItem', dishIndex, itemIndex };
                      const rowKey = `dishItem:${dishIndex}:${itemIndex}`;
                      const itemName = String(item?.displayName || item?.foodName || 'Ингредиент');
                      return (
                        <IngredientEditRow
                          key={rowKey}
                          item={item}
                          themeMode={themeMode}
                          busy={busyKey === rowKey}
                          onChangeWeight={(grams) => void applyEdit({ action: 'set_weight', path, weightGrams: grams }, rowKey)}
                          onDelete={() => void applyEdit({ action: 'delete', path }, rowKey)}
                          onClarify={() => setRefineTarget({ scope: 'selected_item', path, label: itemName })}
                        />
                      );
                    })}
                  </div>
                ) : null}
              </article>
            );
          })}

          {refineTarget ? (
            <article className={`noma-v2-card p-4 ${cardShellClass}`}>
              <div className={`text-[13px] font-bold ${headingClass}`}>Уточнение: {refineTarget.label}</div>
              <input
                type="text"
                autoFocus
                value={refineText}
                onChange={(e) => setRefineText(e.target.value)}
                placeholder="Например: это сырники, а не оладьи"
                className={`mt-2 w-full rounded-[14px] px-4 py-3 text-[14px] font-medium outline-none ${themeMode === 'dark' ? 'bg-[#111319] text-white placeholder:text-[#5b5e6b]' : 'bg-[#f3f3f7] text-[#111113] placeholder:text-[#a1a1ab]'}`}
              />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => { setRefineTarget(null); setRefineText(''); }}
                  className={`min-h-[46px] rounded-[14px] text-[13px] font-extrabold ${themeMode === 'dark' ? 'bg-[#1f222b] text-white' : 'bg-[#f3f3f7] text-[#111113]'}`}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={() => void applyEdit({
                    action: 'refine',
                    scope: refineTarget.scope,
                    userText: refineText.trim(),
                    dishIndex: refineTarget.dishIndex,
                    path: refineTarget.path,
                  }, 'refine')}
                  disabled={refineText.trim().length < 2 || busyKey === 'refine'}
                  className="noma-v2-primary-button flex min-h-[46px] items-center justify-center gap-2 rounded-[14px] text-[13px] disabled:opacity-60"
                >
                  {busyKey === 'refine' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Отправить
                </button>
              </div>
            </article>
          ) : null}

          {error ? <div className="text-[13px] font-semibold text-[#e25c5c]">{error}</div> : null}

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={resetToCapture}
              className={`min-h-[52px] rounded-[18px] text-[14px] font-extrabold ${themeMode === 'dark' ? 'bg-[#1f222b] text-white' : 'bg-[#f3f3f7] text-[#111113]'}`}
            >
              Новое фото
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={isSaving}
              className="noma-v2-primary-button flex min-h-[52px] items-center justify-center gap-2 rounded-[18px] disabled:opacity-60"
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Сохранить
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

export function MealPortionSheet({
  meal,
  consumedPercent,
  isSubmitting,
  themeMode,
  onCancel,
  onChange,
  onConfirm,
}: {
  meal: V2Meal | null;
  consumedPercent: number;
  isSubmitting: boolean;
  themeMode: 'light' | 'dark';
  onCancel: () => void;
  onChange: (value: number) => void;
  onConfirm: (meal: V2Meal, consumedPercent: number) => Promise<void>;
}) {
  if (!meal) return null;

  const preview = getMealPortionPreview(meal, consumedPercent);
  const quickPresets = [100, 75, 50, 25];
  const previewIngredients = preview.ingredients.slice(0, 3);
  const hiddenIngredientsCount = Math.max(0, preview.ingredients.length - previewIngredients.length);
  const sliderProgress = ((preview.consumedPercent - MIN_MEAL_PORTION_PERCENT) / (MAX_MEAL_PORTION_PERCENT - MIN_MEAL_PORTION_PERCENT)) * 100;

  return (
    <div className="noma-v2-modal-backdrop fixed inset-0 z-[74] flex items-end justify-center bg-[rgba(17,17,19,0.34)] px-4 pb-4 pt-10">
      <button type="button" aria-label="Закрыть" className="absolute inset-0 cursor-default" onClick={onCancel} />
      <div
        className={`noma-v2-sheet-enter noma-v2-sheet-enter--bottom relative flex w-full max-w-[360px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-[30px] backdrop-blur-[12px] ${
        themeMode === 'dark'
          ? 'bg-[rgba(24,26,33,0.98)] shadow-[0_28px_60px_rgba(0,0,0,0.34)]'
          : 'bg-[rgba(255,255,255,0.98)] shadow-[0_28px_60px_rgba(24,20,43,0.22)]'
      }`}
        style={{
          ...TOUCH_HOLD_PROTECTION_STYLE,
          maxHeight: 'calc(100vh - 16px)',
        }}
      >
        <div className="flex-1 overflow-y-auto px-4 pb-3 pt-4">
          <div className={`mx-auto h-1.5 w-12 rounded-full ${themeMode === 'dark' ? 'bg-[rgba(255,255,255,0.08)]' : 'bg-[rgba(17,17,19,0.08)]'}`} />
          <div className={`mt-4 text-[24px] font-black tracking-[-0.05em] ${themeMode === 'dark' ? 'text-white' : 'text-[#111113]'}`}>Осталось на тарелке</div>
          <p className={`mt-2 text-[14px] font-semibold leading-6 ${themeMode === 'dark' ? 'text-[#a5a8b8]' : 'text-[#767682]'}`}>
            Покажи, сколько получилось съесть.
          </p>

          <div className={`mt-5 rounded-[24px] p-4 ${themeMode === 'dark' ? 'bg-[rgba(255,255,255,0.04)]' : 'bg-[#f7f7fb]'}`}>
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className={`text-[12px] font-bold uppercase tracking-[0.14em] ${themeMode === 'dark' ? 'text-[#8f95aa]' : 'text-[#90909c]'}`}>Съедено</div>
              <div className={`mt-1 text-[34px] font-black tracking-[-0.06em] ${themeMode === 'dark' ? 'text-white' : 'text-[#111113]'}`}>{preview.consumedPercent}%</div>
            </div>
            <div className="text-right">
              <div className={`text-[12px] font-bold uppercase tracking-[0.14em] ${themeMode === 'dark' ? 'text-[#8f95aa]' : 'text-[#90909c]'}`}>Останется в дневнике</div>
              <div className={`mt-1 text-[22px] font-black tracking-[-0.04em] ${themeMode === 'dark' ? 'text-white' : 'text-[#111113]'}`}>{roundNumber(preview.calories)} ккал</div>
            </div>
          </div>

          <div className="mt-5">
            <input
              type="range"
              min={MIN_MEAL_PORTION_PERCENT}
              max={MAX_MEAL_PORTION_PERCENT}
              step={MEAL_PORTION_STEP}
              value={preview.consumedPercent}
              onChange={(event) => onChange(clampMealConsumedPercent(Number(event.target.value)))}
              disabled={isSubmitting}
              className="noma-v2-portion-slider h-7 w-full cursor-pointer appearance-none bg-transparent"
              style={{
                ['--slider-progress' as any]: `${sliderProgress}%`,
              }}
            />
            <div className={`mt-2 flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.08em] ${themeMode === 'dark' ? 'text-[#8f95aa]' : 'text-[#9a9aa6]'}`}>
              <span>{MIN_MEAL_PORTION_PERCENT}%</span>
              <span>{MAX_MEAL_PORTION_PERCENT}%</span>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-2">
            {quickPresets.map((preset) => {
              const selected = preview.consumedPercent === preset;
              return (
                <button
                  key={preset}
                  type="button"
                  onClick={() => onChange(preset)}
                  disabled={isSubmitting}
                  className={`min-h-[38px] rounded-[14px] text-[13px] font-black tracking-[-0.03em] disabled:opacity-60 ${getSelectablePillClass(themeMode, selected)}`}
                >
                  {preset}%
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          {[
            { label: 'Белки', value: preview.protein, unit: 'г' },
            { label: 'Жиры', value: preview.fats, unit: 'г' },
            { label: 'Углеводы', value: preview.carbs, unit: 'г' },
            { label: 'Вес', value: preview.weight, unit: 'г' },
          ].map((item) => (
            <div key={item.label} className={`rounded-[20px] p-4 ${themeMode === 'dark' ? 'bg-[rgba(255,255,255,0.04)]' : 'bg-[#f7f7fb]'}`}>
              <div className={`text-[12px] font-bold uppercase tracking-[0.08em] ${themeMode === 'dark' ? 'text-[#8f95aa]' : 'text-[#9a9aa6]'}`}>{item.label}</div>
              <div className={`mt-2 text-[22px] font-black tracking-[-0.05em] ${themeMode === 'dark' ? 'text-white' : 'text-[#111113]'}`}>
                {roundNumber(item.value)}
                <span className={`ml-1 text-[13px] font-bold ${themeMode === 'dark' ? 'text-[#8f95aa]' : 'text-[#a0a0aa]'}`}>{item.unit}</span>
              </div>
            </div>
          ))}
          </div>

          {previewIngredients.length > 0 ? (
            <div className={`mt-4 rounded-[22px] p-3 ${themeMode === 'dark' ? 'bg-[rgba(255,255,255,0.04)]' : 'bg-[#f7f7fb]'}`}>
              <div className={`px-1 pb-2 text-[12px] font-bold uppercase tracking-[0.08em] ${themeMode === 'dark' ? 'text-[#8f95aa]' : 'text-[#9a9aa6]'}`}>Состав после пересчёта</div>
              <div className="space-y-2">
                {previewIngredients.map((ingredient) => (
                  <div
                    key={ingredient.id}
                    className={`flex items-center justify-between gap-3 rounded-[16px] px-3 py-2.5 ${
                      themeMode === 'dark' ? 'bg-[rgba(0,0,0,0.12)]' : 'bg-[rgba(255,255,255,0.76)]'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className={`truncate text-[13px] font-black tracking-[-0.03em] ${themeMode === 'dark' ? 'text-white' : 'text-[#111113]'}`}>{ingredient.name}</div>
                      <div className={`mt-1 text-[11px] font-semibold ${themeMode === 'dark' ? 'text-[#8f95aa]' : 'text-[#8f909b]'}`}>
                        {formatIngredientMacroLine(ingredient)}
                      </div>
                    </div>
                    <div className={`shrink-0 text-[13px] font-black tracking-[-0.03em] ${themeMode === 'dark' ? 'text-white' : 'text-[#111113]'}`}>
                      {ingredient.weight ? `${roundNumber(ingredient.weight)} г` : '—'}
                    </div>
                  </div>
                ))}
              </div>
              {hiddenIngredientsCount > 0 ? (
                <div className={`px-1 pt-2 text-[11px] font-semibold ${themeMode === 'dark' ? 'text-[#8f95aa]' : 'text-[#8f909b]'}`}>
                  И ещё {hiddenIngredientsCount}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className={`grid shrink-0 grid-cols-2 gap-3 border-t px-4 pb-4 pt-3 ${themeMode === 'dark' ? 'border-[rgba(255,255,255,0.06)]' : 'border-[rgba(17,17,19,0.06)]'}`}>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className={`min-h-[52px] rounded-[18px] px-4 text-[14px] font-extrabold tracking-[-0.03em] disabled:opacity-60 ${
              themeMode === 'dark' ? 'bg-[#1f222b] text-white' : 'bg-[#f3f3f7] text-[#111113]'
            }`}
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => void onConfirm(meal, preview.consumedPercent)}
            disabled={isSubmitting}
            className="flex min-h-[52px] items-center justify-center gap-2 rounded-[18px] bg-[linear-gradient(135deg,#8b63ff_0%,#7f54ff_100%)] px-4 text-[14px] font-extrabold tracking-[-0.03em] text-white shadow-[0_14px_28px_rgba(127,84,255,0.24)] disabled:opacity-60"
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            <span>Сохранить</span>
          </button>
        </div>
      </div>
    </div>
  );
}
